import {
  Text,
  Badge,
  PreviewCard,
  Skeleton,
  Tooltip,
  Box,
  Flex
} from '@sequencehq/core-components'
import {
  BorderRadius4,
  GreyGrey20,
  GreyGrey70,
  GreyGrey80,
  IndigoIndigo50,
  Primary13Regular,
  Primary13SemiBold
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { toBillingScheduleStatusBadgeProps } from '@sequencehq/utils'
import { useFetchCustomerQuery, getLogoSrc } from 'Customer/index'
import { dateTimeWithFormat, formatScheduleEndDate } from '@sequencehq/utils'

export const PhaseBar = ({
  phases,
  scheduleStatus
}: {
  phases?: Array<{
    id: string
    name?: string
    startDate: string
    endDate: string
  }>
  scheduleStatus?: string
}) => {
  if (!phases || phases.length === 0) {
    return (
      <Box
        height="8px"
        width="100%"
        backgroundColor={GreyGrey20}
        borderRadius={BorderRadius4}
      />
    )
  }

  const now = new Date()
  const isScheduleStarted = scheduleStatus && scheduleStatus !== 'DRAFT'

  return (
    <Flex height="8px" width="100%" gap="4px">
      {phases.map((phase, index) => {
        const phaseStart = new Date(phase.startDate)
        const phaseEnd = new Date(phase.endDate)

        // Check if phase is open-ended (no end date, null, or very distant end date)
        const isOpenEnded =
          !phase.endDate ||
          phase.endDate === null ||
          phaseEnd.getFullYear() > 2100

        const isCompleted = !isOpenEnded && now > phaseEnd
        const isInProgress =
          now >= phaseStart && (isOpenEnded || now <= phaseEnd)

        let progressPercentage = 0
        if (isScheduleStarted) {
          if (isOpenEnded && isInProgress) {
            progressPercentage = 100 // Fill completely for open-ended active phases
          } else if (isCompleted) {
            progressPercentage = 100
          } else if (isInProgress) {
            const totalDuration = phaseEnd.getTime() - phaseStart.getTime()
            const elapsed = now.getTime() - phaseStart.getTime()
            progressPercentage = Math.min(
              Math.max((elapsed / totalDuration) * 100, 0),
              100
            )
          }
        }

        const phaseName = phase.name || `Phase ${index + 1}`
        const dateRange = `${dateTimeWithFormat(phase.startDate, 'd MMM yyyy')} - ${
          isOpenEnded
            ? 'Open-ended'
            : dateTimeWithFormat(phase.endDate, 'd MMM yyyy')
        }`

        const tooltipLabel = (
          <div>
            <div style={{ ...Primary13SemiBold, marginBottom: '4px' }}>
              {phaseName}
            </div>
            <div>{dateRange}</div>
          </div>
        )

        return (
          <Tooltip
            key={phase.id}
            label={tooltipLabel}
            placement="top"
            shouldWrapChildren={false}
          >
            <Box
              flex="1"
              height="16px"
              display="flex"
              alignItems="center"
              cursor="default"
            >
              <Box
                width="100%"
                backgroundColor={GreyGrey20}
                borderRadius={BorderRadius4}
                position="relative"
                overflow="hidden"
                height="8px"
              >
                {isScheduleStarted && (isInProgress || isCompleted) && (
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    height="100%"
                    width={`${progressPercentage}%`}
                    backgroundColor={
                      isOpenEnded && isInProgress
                        ? 'transparent'
                        : IndigoIndigo50
                    }
                    borderRadius={BorderRadius4}
                    {...(isOpenEnded && isInProgress
                      ? {
                          backgroundImage: `repeating-linear-gradient(
                      -45deg,
                      rgba(99, 102, 241, 0.5) 0px,
                      rgba(99, 102, 241, 0.5) 1px,
                      transparent 1px,
                      transparent 4px
                    )`,
                          border: `1px solid ${IndigoIndigo50}`
                        }
                      : {})}
                  />
                )}
              </Box>
            </Box>
          </Tooltip>
        )
      })}
    </Flex>
  )
}

export const BillingSchedulePreviewCard = ({
  scheduleId,
  customerId,
  showViewScheduleButton = true,
  showBorder = true,
  onClick,
  'data-testid': dataTestId
}: {
  scheduleId: string
  customerId?: string
  /** Show the "View schedule" button (default). Set false to hide it, e.g. when
   * the whole card is clickable via `onClick` instead. */
  showViewScheduleButton?: boolean
  /** Set false inside a popover, which draws its own border - two of them read as a double line. */
  showBorder?: boolean
  /** Makes the whole card clickable (used instead of the button in some flows). */
  onClick?: () => void
  'data-testid'?: string
}) => {
  const testId = dataTestId ?? `billingSchedules.preview.${scheduleId}`

  const billingScheduleQuery = useQuery(
    dashboard20240730Client.getBillingSchedule,
    { id: scheduleId },
    {
      select: data => {
        if (!data) return
        return {
          customerId: data.customerId,
          status: data.status,
          startDate: data.startDate,
          endDate: data.endDate,
          phases: data.phases
        }
      }
    }
  )

  const customerQuery = useFetchCustomerQuery(
    customerId ?? billingScheduleQuery.data?.customerId
  )

  const logoSrc = customerQuery.customerDetails
    ? getLogoSrc(customerQuery.customerDetails)
    : undefined

  if (billingScheduleQuery.isLoading || customerQuery.loading) {
    return (
      <PreviewCard
        data-testid={testId}
        logoSrc={logoSrc}
        title={
          <Flex alignItems="center" justifyContent="space-between" width="100%">
            <Box>
              <Skeleton
                height="16px"
                width="120px"
                minWidth="120px"
                minHeight="16px"
                borderRadius="4px"
              />
              <Box mt={1}>
                <Skeleton
                  height="14px"
                  width="100px"
                  minWidth="100px"
                  minHeight="14px"
                  borderRadius="4px"
                />
              </Box>
            </Box>
            <Skeleton
              height="20px"
              width="60px"
              minWidth="60px"
              minHeight="20px"
              borderRadius="12px"
            />
          </Flex>
        }
        content={
          <>
            {/* Phase Bar Skeleton */}
            <Box paddingX="8px" paddingBottom="8px" paddingTop="2px">
              <Skeleton height="8px" borderRadius="4px" width="100%" />
            </Box>

            <Flex
              paddingX="8px"
              paddingBottom="8px"
              justifyContent="space-between"
            >
              <Skeleton
                height="16px"
                width="80px"
                minWidth="64px"
                borderRadius="4px"
              />
              <Skeleton
                height="16px"
                width="100px"
                minWidth="64px"
                borderRadius="4px"
              />
            </Flex>
          </>
        }
        showBorder={showBorder}
        fullWidth
      />
    )
  }

  const title = (
    <Flex alignItems="center" justifyContent="space-between" width="100%">
      <Flex flexDir="column" gap="4px">
        <Text color={GreyGrey80} {...Primary13SemiBold}>
          Billing schedule
        </Text>
        <Text color={GreyGrey70} {...Primary13Regular}>
          {customerQuery.customerDetails?.legalName ?? '-'}
        </Text>
      </Flex>
      <Badge
        {...toBillingScheduleStatusBadgeProps({
          status: billingScheduleQuery.data?.status ?? 'DRAFT'
        })}
      />
    </Flex>
  )

  const content = (
    <>
      {/* Phase Bar */}
      <Box paddingX="8px" paddingBottom="8px">
        <PhaseBar
          phases={billingScheduleQuery.data?.phases}
          scheduleStatus={billingScheduleQuery.data?.status}
        />
      </Box>

      {/* Schedule Dates */}
      {billingScheduleQuery.data && (
        <Flex paddingX="8px" paddingBottom="8px" justifyContent="space-between">
          <Text color={GreyGrey70} {...Primary13Regular}>
            {dateTimeWithFormat(
              billingScheduleQuery.data.startDate,
              'd MMM yyyy'
            )}
          </Text>
          <Text color={GreyGrey70} {...Primary13Regular}>
            {formatScheduleEndDate(billingScheduleQuery.data.endDate)}
          </Text>
        </Flex>
      )}
    </>
  )

  return (
    <PreviewCard
      data-testid={testId}
      logoSrc={logoSrc}
      title={title}
      content={content}
      button={
        showViewScheduleButton
          ? {
              label: 'View schedule',
              to: `/billing-schedules/${scheduleId}`
            }
          : undefined
      }
      onClick={onClick}
      showBorder={showBorder}
      fullWidth
    />
  )
}
